/**
 * Cross-Domain Authentication Debugger
 * Diagnostic tool to identify and log cross-domain authentication issues
 */

export interface CrossDomainDebugInfo {
  domain: string;
  subdomain: string;
  cookieDomain: string;
  sameSite: string;
  secure: boolean;
  sessionTokens: {
    employee: boolean;
    client: boolean;
  };
  apiEndpoint: string;
  timestamp: string;
}

/**
 * Enhanced cookie domain detection for cross-domain compatibility
 */
export function getCookieDomainForDomain(currentDomain: string): string {
  // Handle localhost and development domains
  if (
    currentDomain === 'localhost' ||
    currentDomain === '127.0.0.1' ||
    currentDomain === '::1' ||
    currentDomain === '0.0.0.0'
  ) {
    return '';
  }

  // Dual-host sandbox: portal (sandbox.*) and API (api.sandbox.*) must stay
  // host-only. Domain=.mechagora.net would share cookies with api.sandbox and
  // other tenants under mechagora.net — never set Domain for sandbox hosts.
  if (
    currentDomain === 'sandbox.mechagora.net' ||
    currentDomain === 'www.sandbox.mechagora.net' ||
    currentDomain.endsWith('.sandbox.mechagora.net') ||
    currentDomain.endsWith('.mechagora.net')
  ) {
    return '';
  }

  // Handle development subdomains like trading-portal-dev.taildd48ba.ts.net
  if (currentDomain.includes('taildd48ba.ts.net')) {
    return '.taildd48ba.ts.net';
  }

  // Production brand domain only (not mechagora.net sandbox plane)
  if (currentDomain === 'mechagora.com' || currentDomain.endsWith('.mechagora.com')) {
    // Prefer host-only for app/api split; cross-subdomain SSO uses BFF cookie on api.*
    // Keep empty so sessionToken is not shared with api.mechagora.com by Domain=.
    return '';
  }

  return '';
}

/**
 * Check if current domain configuration supports cross-subdomain cookies
 */
export function validateCrossDomainCookieSupport(): {
  isSupported: boolean;
  issues: string[];
  recommendations: string[];
} {
  const issues: string[] = [];
  const recommendations: string[] = [];
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : '';

  // Check if domain supports cross-subdomain cookies
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    // Local development - should work with no domain restriction
    return { isSupported: true, issues: [], recommendations: [] };
  }

  const cookieDomain = getCookieDomainForDomain(currentDomain);

  if (!cookieDomain) {
    issues.push(`No suitable cookie domain found for: ${currentDomain}`);
    recommendations.push('Configure explicit cookie domain in authentication endpoints');
  }

  // Check if current domain matches expected cookie domain pattern
  if (!currentDomain.endsWith(cookieDomain.replace('.', ''))) {
    issues.push(`Current domain ${currentDomain} may not be compatible with cookie domain ${cookieDomain}`);
    recommendations.push('Ensure all subdomains use consistent cookie domain configuration');
  }

  return {
    isSupported: issues.length === 0,
    issues,
    recommendations
  };
}

/**
 * Debug current authentication state across domains
 */
export function debugCrossDomainAuthState(): CrossDomainDebugInfo {
  const domain = typeof window !== 'undefined' ? window.location.hostname : '';
  const subdomain = domain.split('.')[0] || '';
  const cookieDomain = getCookieDomainForDomain(domain);

  // Check for session tokens
  const employeeToken = typeof window !== 'undefined'
    ? document.cookie.includes('sessionToken=')
    : false;
  const clientToken = typeof window !== 'undefined'
    ? document.cookie.includes('clientSessionToken=')
    : false;

  return {
    domain,
    subdomain,
    cookieDomain,
    sameSite: 'strict', // Current configuration
    secure: process.env.NODE_ENV === 'production',
    sessionTokens: {
      employee: employeeToken,
      client: clientToken
    },
    apiEndpoint: process.env.NEXT_PUBLIC_API_BASE_URL || 'relative',
    timestamp: new Date().toISOString()
  };
}

/**
 * Log cross-domain authentication diagnostics
 */
export function logCrossDomainDiagnostics(): void {
  console.group('🔍 Cross-Domain Authentication Diagnostics');

  const debugInfo = debugCrossDomainAuthState();
  const validation = validateCrossDomainCookieSupport();

  console.log('📋 Current Domain Info:', debugInfo);
  console.log('✅ Cookie Support Validation:', validation);

  if (!validation.isSupported) {
    console.warn('⚠️  Cross-domain authentication issues detected:');
    validation.issues.forEach(issue => console.warn(`   - ${issue}`));
    console.log('💡 Recommendations:');
    validation.recommendations.forEach(rec => console.log(`   - ${rec}`));
  }

  console.groupEnd();
}

/**
 * Enhanced cookie configuration for cross-domain compatibility
 */
export function getCrossDomainCookieConfig(domain: string): {
  domain: string;
  sameSite: 'lax' | 'strict' | 'none';
  secure: boolean;
} {
  const cookieDomain = getCookieDomainForDomain(domain);
  const isSecure = process.env.NODE_ENV === 'production' || domain !== 'localhost';

  return {
    domain: cookieDomain,
    sameSite: isSecure ? 'lax' : 'strict', // Use lax for cross-subdomain compatibility
    secure: isSecure
  };
}

/**
 * Check if middleware restrictions will block authentication
 */
export function checkMiddlewareRestrictions(): {
  isBlocked: boolean;
  reason?: string;
  domain: string;
} {
  const domain = typeof window !== 'undefined' ? window.location.hostname : '';

  // Check if domain is considered "internal" by middleware
  const isInternalDomain = domain === 'localhost' ||
    domain === '127.0.0.1' ||
    domain.endsWith('.local') ||
    domain.includes('taildd48ba.ts.net');

  if (!isInternalDomain && domain.endsWith('mechagora.com')) {
    return {
      isBlocked: true,
      reason: 'Domain not recognized as internal by middleware',
      domain
    };
  }

  return {
    isBlocked: false,
    domain
  };
}